import { Component, OnChanges, OnDestroy, Input, SimpleChanges, HostListener, ViewChild, ElementRef, OnInit } from '@angular/core';

import {Subject, Subscription, takeUntil, tap } from 'rxjs';

import { VehicleControlIndicator } from '../../../../commons/models/domain/VehicleControlIndicator';
import { Vehicle } from '../../../../commons/models/domain/Vehicle';
import { Driver } from '../../../../commons/models/domain/Driver';
import { VehicleIndicatorTypeEnum } from '@commons/enums/vehicle-indicator-type.enum';
import { MapService } from '@commons/services/map/map/map.service';
import { MapConfigService } from '@commons/services/map/config/map-config.service';
import { ParameterTypeEnum } from '@commons/enums/parameter-type.enum';
import { UpdateVehicleNameRequest } from '../../../../commons/models/remote.request/UpdateVehicleNameRequest';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '@shared/services/permission.service';
import { DialogService } from 'primeng/dynamicdialog';
import { VehicleChangeNameFormComponent } from '@src/app/standalone-components/vehicle/vehicle-change-name-form/vehicle-change-name-form.component';
import { MODAL_DEFAULT_CONFIG } from '@src/app/utils/ngprime-components-config';
import { Destroyable } from '@src/app/utils/Destroyable';

@Component({
    selector: 'tk-vehicle-details-header',
    styleUrls: ['./vehicle-details-header.component.scss'],
    templateUrl: './vehicle-details-header.component.html',
    providers: [DialogService],
})
export class VehicleDetailsHeaderComponent extends Destroyable implements OnChanges, OnDestroy, OnInit {

  @ViewChild('vehicleName')inputRef?: ElementRef

  @HostListener('keydown.enter', ['$event'])
   saveByKeyboard(){
     if(this.editMode){
       this.saveVehicleName(this.inputRef?.nativeElement.value)
     }
   }

   @HostListener('keydown.esc', ['$event'])
   leaveEdit()
   {
     if(this.editMode){
       this.editMode = false
     }
   }

    /**
      * Vehicle
      */
    @Input() public vehicle?: Vehicle;

    /**
     * Driver
     */
    public driver?: Driver;

    /**
     * Driver subscription
     */
    public driverSub?: Subscription;

    /**
     * Subject for pushing vehicle value changes
     */
    public vehicleValueChangeSubject: Subject<Vehicle> = new Subject<Vehicle>();

    /**
     *
     */
    private readonly paramTypesToShow = [1, 100, 7];

    /**
     * Vehicle params
     */
    public params: VehicleControlIndicator[] = [];

    /**
     * Vehicle params subscription
     */
    private paramsSub: Subscription;

    /**
     * Speed indicator
     */
    public speed?: VehicleControlIndicator;

    /**
     * fuel indicator
     */
    public fuel?: VehicleControlIndicator;

    /**
     * fuel indicator
     */
    public engineSpeed?: VehicleControlIndicator;


    /**
     *
     */
    public selectedDotIndex = 0;

    /**
     *
     */
    public VehicleIndicatorType = VehicleIndicatorTypeEnum;

    /**
    * czy nazwa pojazdu jest w trybie edycji
    */
    public editMode: boolean = false;

    public editValue?: string

    /**
    * Returns analog params
    */
    public get analogParams(): VehicleControlIndicator[] {
        return this.params ? this.params.filter(param => param && param.LV.V !== 2) : [];
    }

    /**
    * Returns  binary params
    */
    public get binaryParams(): VehicleControlIndicator[] {
        return this.params ? this.params.filter(param => param && param.LV.V === 2) : [];
    }

    public selectDotIndex(idx: number) {
        this.selectedDotIndex = idx;
    }

    public hasPermissionToEditVehicleName: boolean;

    constructor(
        private $map: MapService,
        private $mapConfig: MapConfigService,
        private $vehicleService: VehicleService,
        private translate: TranslateService,
        private permissionService: PermissionService,
        private dialogService: DialogService,
    ) {
        super();
        // pobranie danych
        this.paramsSub = this.vehicleValueChangeSubject
            .subscribe(result => {
                result.ControlIndicators = result.ControlIndicators || [];

                //  przepisanie konkretnych
                this.speed = result.ControlIndicators.find(p => p && p.Tp === ParameterTypeEnum.Speed);
                if (!this.speed) {
                    this.speed = this.createVehicleControlIndicator(ParameterTypeEnum.Speed, 'Prędkość', 0);
                }
                else if (!this.speed.LV) {
                    this.speed.LV = <any>{ V: 0 };
                }

                this.fuel = result.mainFuel;
                if (!this.fuel) {
                    this.fuel = this.createVehicleControlIndicator(ParameterTypeEnum.Fuel, "Paliwo", 0);
                }
                else if (!this.fuel.LV) {
                    this.fuel.LV = <any>{ V: 0 };
                }

                this.engineSpeed = result.ControlIndicators.find(p => p && p.Tp === ParameterTypeEnum.EngineSpeed);
                if (!this.engineSpeed) {
                    this.engineSpeed = this.createVehicleControlIndicator(ParameterTypeEnum.EngineSpeed, "Obroty silnika", 0);
                }
                else if (!this.engineSpeed.LV) {
                    this.engineSpeed.LV = <any>{ V: 0 };
                }

                this.params = [];
                // for (const paramType of this.paramTypesToShow) {
                //     const param = result.ControlIndicators.find(p => p && p.Type === paramType);
                //     if (param) {
                //         this.params.push(param);
                //     }
                // }
            });
    }

    ngOnInit(): void {
        this.hasPermissionToEditVehicleName = this.permissionService.hasCardFiles_FCardCars();
    }

    createVehicleControlIndicator(paramType: number, name: string, lastValue: number): VehicleControlIndicator {
        let indicator = {} as VehicleControlIndicator;
        indicator.Tp = paramType;
        indicator.Nm = name;
        indicator.LV = <any>{ V: lastValue };
        return indicator;
    }

    /**
     * Ng on changes lifecycle hook
     */
    public ngOnChanges(changes: SimpleChanges): void {
        // Push new vehicle
        if (changes && changes['vehicle']) {
            const value = changes['vehicle'].currentValue;
            if (value) {
              // console.log(' details = this.vehicle?.DR' + this.vehicle?.DR);
                this.vehicleValueChangeSubject.next(new Vehicle(value, this.translate));
            }
        }
    }

    /**
     * Ng on destroy lifecycle hook
     */
    public override ngOnDestroy(): void {
        // odwiazanie ewentualnej istniejacje
        if (this.paramsSub) {
            this.paramsSub.unsubscribe();
        }
        this.destroy();
    }

    public getSpeedIndicatorValue(speed: VehicleControlIndicator): number {
        if (!speed || !speed.LV) {
            return 220;
        }

        const value = speed.LV.V;
        return Math.round(220.0 - (value / 200.0 * 220.0));
    }


    public closeClick() {
        this.$mapConfig.setCurrentVehicleId('tk-vehicle-details', undefined);
        this.$map.centerMap();
    }

    public saveVehicleName(vehicleName: string) {
        this.updateVehicleName(vehicleName);
        this.editMode = false;

    }

    public closeEditVehicleName() {
        this.editMode = false;
    }

    public showEditVehicleNameModal(): void {
        const ref = this.dialogService.open(VehicleChangeNameFormComponent, {
            ...MODAL_DEFAULT_CONFIG,
            data: this.vehicle,
        });
        ref.onClose.pipe(takeUntil(this.destroy$)).subscribe({
            next: (data) => {
                if (data) {
                    this.updateVehicleName(data);
                }
            }
        });
    }

    updateVehicleName(newVehicleName: string) {
        const request: UpdateVehicleNameRequest = {
            IdVehicle: this.vehicle!.Id!,
            VehicleName: newVehicleName
        };

        this.$vehicleService.updateVehicleName(request)
        .pipe(tap(result=>{
          this.vehicle!.Nm = newVehicleName
        }))
        .subscribe();
    }

}
